import { UTCDate } from '@date-fns/utc';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { LegalEntityCountry } from '@seapoint/server/enums';
import { addDays, format } from 'date-fns';
import { AlertCircle, Dot } from 'lucide-react';
import { ComponentProps } from 'react';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';

// https://docs.yapily.com/pages/tools-and-services/yapilyconnect/yapilyconnect-ux-ais-guidelines/#introduction
export const GB_CONSENT_DURATION_DAYS = 90;
export const EEA_CONSENT_DURATION_DAYS = 180;

const TODAY = new UTCDate();
const DATE_FORMAT = 'dd/MM/yyyy';

// https://docs.yapily.com/pages/tools-and-services/yapilyconnect/yapilyconnect-ux-ais-guidelines/#step-3-display-the-account-information-request-summary
const YAPILY_TERMS_AND_CONDITIONS_URL = 'https://www.yapily.com/legal/end-user-terms';
const YAPILY_PRIVACY_NOTICE_URL = 'https://www.yapily.com/legal/privacy-policy';

type ConnectAccountDialogProps = {
  isOpen: boolean;
  closePopup: () => void;
  institutionName: string;
  institutionLogo: string;
  country: LegalEntityCountry;
  isFetchingConnectionLink: boolean;
  isInstitutionConnected: boolean;
  connectionError?: Error;
  onConnectAccounts: () => void;
  psuIdProvided?: boolean;
};

export const ConnectAccountDialog = ({
  isOpen,
  closePopup,
  institutionName,
  institutionLogo,
  country,
  isFetchingConnectionLink,
  isInstitutionConnected,
  connectionError,
  onConnectAccounts,
  psuIdProvided = false,
}: ConnectAccountDialogProps) => {
  const { descriptionElement, ctaText, onCtaClick } = getCtaContent({
    onConnectAccounts,
    country,
    institutionName,
    psuIdProvided,
  });

  return (
    <Dialog open={isOpen} onClose={closePopup} transition>
      <DialogBackdrop className="fixed inset-0 bg-black/70" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-xl bg-white">
          <ConnectionStatusBadge
            isInstitutionConnected={isInstitutionConnected}
            hasConnectionError={!!connectionError}
          />

          <div className="flex justify-start gap-3 p-5">
            <img src={institutionLogo} alt="" loading="eager" fetchPriority="high" className="h-12 w-12 rounded-full" />

            <div className="flex flex-col justify-start gap-1">
              <DialogTitle as="h3" className="text-sm font-medium leading-5 text-black">
                {institutionName}
              </DialogTitle>

              {descriptionElement}
            </div>
          </div>

          {connectionError && (
            <div className="mx-5 mb-3 flex gap-2 rounded-lg bg-red-50 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
              <div>
                <p className="text-xs font-medium text-red-900">Connection Failed</p>
                <p className="mt-1 text-xs text-red-700">{connectionError.message}</p>
              </div>
            </div>
          )}

          <Inline space={2} className="bg-seapoint-gray-medium p-5">
            {isFetchingConnectionLink ? (
              <Button isLoading={true} className="flex-1">
                Connecting the Bank...
              </Button>
            ) : (
              <Button onClick={onCtaClick} className="flex-1" disabled={!!connectionError}>
                {ctaText}
              </Button>
            )}

            <Button variant="outline" onClick={closePopup} disabled={isFetchingConnectionLink}>
              Cancel
            </Button>
          </Inline>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

type GetCtaContentProps = {
  onConnectAccounts: () => void;
  country: ComponentProps<typeof ConnectAccountDialog>['country'];
  institutionName: ComponentProps<typeof ConnectAccountDialog>['institutionName'];
  psuIdProvided?: boolean;
};

const getCtaContent = ({ onConnectAccounts, country, institutionName, psuIdProvided }: GetCtaContentProps) => {
  if (country === LegalEntityCountry.GB) {
    const expirationDateGB = addDays(TODAY, GB_CONSENT_DURATION_DAYS);
    const formattedExpirationDateGB = format(expirationDateGB, DATE_FORMAT);

    return {
      descriptionElement: (
        <Stack space={3}>
          <p className="text-xs text-seapoint-gray">
            {psuIdProvided
              ? `We're ready to connect your ${institutionName} account with the details you provided. We have partnered with Yapily Connect to access your bank data and act as their agent when we share this information back to you.`
              : `We have partnered with Yapily Connect to access your bank data at ${institutionName} and act as their agent when we share this information back to you.`}
          </p>

          <p className="text-xs text-seapoint-gray">
            {`You will now be securely redirected to ${institutionName} to give access to the following information:`}
          </p>

          <ul className="list-inside list-disc">
            <li className="text-xs text-seapoint-gray">Account(s) details</li>
            <li className="text-xs text-seapoint-gray">Balances</li>
            <li className="text-xs text-seapoint-gray">Transaction history</li>
          </ul>

          <p className="text-xs text-seapoint-gray">
            By using the service, you agree to Yapily Connect accessing your bank data, the{' '}
            <a
              href={YAPILY_TERMS_AND_CONDITIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-seapoint-gray-text"
            >
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a
              href={YAPILY_PRIVACY_NOTICE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-seapoint-gray-text"
            >
              Privacy Notice
            </a>
            .
          </p>

          <p className="text-xs text-seapoint-gray">
            {`
            This consent will automatically expire on ${formattedExpirationDateGB}.`}
          </p>
        </Stack>
      ),
      ctaText: 'Confirm',
      onCtaClick: onConnectAccounts,
    };
  }

  const expirationDateEEA = addDays(TODAY, EEA_CONSENT_DURATION_DAYS);
  const formattedExpirationDateEEA = format(expirationDateEEA, DATE_FORMAT);

  return {
    descriptionElement: (
      <Stack space={3}>
        <p className="text-xs text-seapoint-gray">
          {psuIdProvided
            ? `We're ready to connect your ${institutionName} account with the details you provided. We have partnered with Yapily Connect to access your bank data.`
            : `We have partnered with Yapily Connect to access your bank data at ${institutionName}.`}
        </p>

        <p className="text-xs text-seapoint-gray">
          {`You will now be securely redirected to ${institutionName} to give access to the following information:`}
        </p>

        <ul className="list-inside list-disc">
          <li className="text-xs text-seapoint-gray">Account(s) details</li>
          <li className="text-xs text-seapoint-gray">Balances</li>
          <li className="text-xs text-seapoint-gray">Transaction history</li>
        </ul>

        <p className="text-xs text-seapoint-gray">
          By using the service, you agree to Yapily Connect accessing your bank data, the{' '}
          <a
            href={YAPILY_TERMS_AND_CONDITIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-seapoint-gray-text"
          >
            Terms & Conditions
          </a>{' '}
          and{' '}
          <a
            href={YAPILY_PRIVACY_NOTICE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-seapoint-gray-text"
          >
            Privacy Notice
          </a>
          .
        </p>

        <p className="text-xs text-seapoint-gray">{`This consent will be valid until ${formattedExpirationDateEEA}.`}</p>
      </Stack>
    ),
    ctaText: 'Confirm',
    onCtaClick: onConnectAccounts,
  };
};

type ConnectionStatusBadgeProps = {
  isInstitutionConnected: boolean;
  hasConnectionError: boolean;
};

const ConnectionStatusBadge = ({ isInstitutionConnected, hasConnectionError }: ConnectionStatusBadgeProps) => {
  if (!isInstitutionConnected && !hasConnectionError) {
    return null;
  }

  const variant = isInstitutionConnected ? 'success' : 'error';
  const { badgeText, iconColor } = getBadgeContent(variant);

  return (
    <div className="absolute right-4 top-2 flex items-center">
      <Dot color={iconColor} size={24} />

      <p className="text-[10px] text-black">{badgeText}</p>
    </div>
  );
};

const getBadgeContent = (variant: 'success' | 'error') => {
  switch (variant) {
    case 'success':
      return { badgeText: 'Connected', iconColor: '#82CC69' };
    case 'error':
      return { badgeText: 'Connection error', iconColor: '#FF5318' };
    default:
      return exhaustiveCheck(variant);
  }
};

// Default argument for switch statements so TypeScript notifies if we fail to cover a case
const exhaustiveCheck = (arg: never) => arg;
